import { AudioAnalyzer } from "../audio/audio-analyzer";
import { LocalAudioManager } from "../audio/audio-manager";
import type { WebAudioRecorder } from "../audio/audio-recorder";
import { AutoCommentTrigger } from "../comment-generation/auto-comment-trigger";
import {
	CommentSystem,
	DEFAULT_COMMENT_SYSTEM_CONFIG,
} from "../comment-generation/comment-system";
import { DEFAULT_USER_ID } from "../constants/user";
import {
	createError,
	ErrorSeverity,
	ErrorType,
	errorHandler,
} from "../error-handling";
import type { PresetManager } from "../interfaces/preset-management";
import type { SummaryGenerator } from "../interfaces/summary-generation";
import type { AppCallbacks } from "../main-service";
import { lazyLoader } from "../performance/lazy-loader";
import type { PreferenceManager } from "../preferences/preference-manager";
import { SessionManagerImpl } from "../session/session-manager-impl";
import { IndexedDBWrapper } from "../storage/indexeddb-wrapper";
import type { GeminiClientImpl } from "../summary/ai-client";
import type { SummaryGeneratorImpl } from "../summary/summary-generator-impl";
import type { Session } from "../types/core";
import { DEFAULT_COMMENTER_DEFINITIONS } from "../types/core";
import type { TranscriptSegment as UITranscriptSegment } from "../ui/components/TranscriptionDisplay";
import { WebSpeechVoiceInputManager } from "../voice/voice-input-manager";

/**
 * アプリケーションの各コンポーネント間の連携とライフサイクルを管理するコーディネーター
 */
export class AppCoordinator {
	public storage: IndexedDBWrapper;
	public voiceManager: WebSpeechVoiceInputManager;
	public commentSystem!: CommentSystem;
	public preferenceManager!: PreferenceManager;
	public summaryGenerator!: SummaryGenerator;
	public sessionManager!: SessionManagerImpl;
	public audioManager: LocalAudioManager;
	public audioRecorder!: WebAudioRecorder;
	public audioAnalyzer: AudioAnalyzer;
	public presetManager!: PresetManager;

	public isRunning = false;
	public isPaused = false;
	public currentSessionId: string | null = null;
	public hasInterimResult = false;
	public currentTranscriptId: string | null = null;
	public currentTranscriptStartTime: Date | null = null;
	/** 自動コメントトリガー制御 */
	public autoCommentTrigger: AutoCommentTrigger;

	private callbacks: AppCallbacks;

	constructor(callbacks: AppCallbacks) {
		this.callbacks = callbacks;
		this.storage = new IndexedDBWrapper();
		this.voiceManager = new WebSpeechVoiceInputManager();
		this.audioManager = new LocalAudioManager(this.storage);
		this.audioAnalyzer = new AudioAnalyzer();
		this.autoCommentTrigger = new AutoCommentTrigger();
	}

	/**
	 * システム全体の初期化フローを実行する
	 */
	async initialize(): Promise<void> {
		try {
			// 1. 基本インフラの準備
			if (!this.voiceManager.isSupported()) {
				await errorHandler.handleError(
					createError(
						ErrorType.BROWSER_COMPATIBILITY,
						ErrorSeverity.HIGH,
						"Web Speech API (Voice Input) is not supported in this browser.",
					),
				);
			}
			await this.storage.initialize();

			// 2. ユーザー設定・プリセットの初期化
			const PreferenceManagerClass = (await lazyLoader.loadFeature(
				"preference-manager",
			)) as typeof PreferenceManager;
			this.preferenceManager = new PreferenceManagerClass(this.storage);

			const { PresetManagerImpl } = await import(
				"../preferences/preset-manager-impl.js"
			);
			this.presetManager = new PresetManagerImpl(this.storage);
			await this.presetManager.initializeRecommendedPresets();

			// 3. 各種エンジンのロード（遅延読み込み）
			const [AudioRecorderClass, SummaryGeneratorClass, GeminiClientClass] =
				(await Promise.all([
					lazyLoader.loadFeature("audio-recording"),
					lazyLoader.loadFeature("summary-generator"),
					import("../summary/ai-client.js").then((m) => m.GeminiClientImpl),
				])) as [
					{ new (): WebAudioRecorder },
					{ new (client: GeminiClientImpl): SummaryGeneratorImpl },
					{ new (): GeminiClientImpl },
				];

			this.audioRecorder = new AudioRecorderClass();
			this.summaryGenerator = new SummaryGeneratorClass(
				new GeminiClientClass(),
			);
			this.sessionManager = new SessionManagerImpl(
				this.storage,
				this.summaryGenerator,
			);

			// 4. コメントシステムの構築と配線
			this.commentSystem = new CommentSystem(
				{ ...DEFAULT_COMMENT_SYSTEM_CONFIG, enableLocalLLM: true },
				this.preferenceManager,
			);
			await this.commentSystem.initialize(this.storage, this.preferenceManager);
			this.setupCommentSystemCallbacks();

			// 5. デフォルト設定の適用
			await this.applyInitialPreferences();

			// 6. イベントハンドラのセットアップ
			this.setupInternalEventHandlers();

			this.callbacks.onStatusChange("システム準備完了", "ready");
		} catch (error) {
			await errorHandler.handleError(
				createError(
					ErrorType.STORAGE,
					ErrorSeverity.CRITICAL,
					`Initialization failed: ${error}`,
				),
			);
			this.callbacks.onStatusChange("初期化に失敗しました", "error");
		}
	}

	/**
	 * システム全体をクリーンアップする
	 */
	public async destroy(): Promise<void> {
		this.isRunning = false;

		// 各コンポーネントのクリーンアップ
		if (this.voiceManager) {
			this.voiceManager.stopListening();
		}

		if (this.audioAnalyzer) {
			this.audioAnalyzer.destroy();
		}

		if (this.audioRecorder) {
			// WebAudioRecorder uses dispose()
			this.audioRecorder.dispose();
		}

		if (this.commentSystem) {
			await this.commentSystem.destroy();
		}

		if (this.storage) {
			await this.storage.close();
		}

		this.currentSessionId = null;
		this.currentTranscriptId = null;
		this.currentTranscriptStartTime = null;
	}

	public clearTranscription(): void {
		this.currentTranscriptId = null;
		this.currentTranscriptStartTime = null;
		this.hasInterimResult = false;
		this.callbacks.onTranscriptionClear();
	}

	public async resumeStoredSession(sessionId: string): Promise<Session> {
		const session = await this.sessionManager.resumeStoredSession(sessionId);
		this.currentSessionId = session.id;

		// Notify UI about loaded transcripts
		const uiSegments: UITranscriptSegment[] = session.transcript.map((s) => ({
			id: s.id || `restored-${Math.random()}`,
			text: s.text,
			isFinal: s.isFinal,
			timestamp: new Date(session.startTime.getTime() + s.start),
			type: s.type || "speech",
			confidence: s.confidence,
			commenterId: s.commenterId,
		}));

		this.callbacks.onTranscriptionClear();
		this.callbacks.onTranscriptionBulkUpdate(uiSegments);
		this.callbacks.onCommentsUpdate(session.comments);
		this.callbacks.onSessionIdChange(session.id);

		return session;
	}

	private setupCommentSystemCallbacks(): void {
		this.commentSystem.onInteraction = (interaction) => {
			if (!this.currentSessionId) return;

			this.sessionManager.addUserInteraction(
				this.currentSessionId,
				interaction,
			);
			if (interaction.type === "pin") {
				const session = this.sessionManager.getCurrentSession(DEFAULT_USER_ID);
				const comment = session?.comments.find(
					(c) => c.id === interaction.commentId,
				);
				if (comment && session) {
					const segmentId = `comment-${comment.id}-${Date.now()}`;
					const timestamp = new Date();
					const elapsed = this.sessionManager.getEffectiveElapsedMs(
						this.currentSessionId,
						timestamp,
					);
					const segment: UITranscriptSegment = {
						id: segmentId,
						text: comment.content,
						isFinal: true,
						type: "comment",
						timestamp: timestamp,
						confidence: 1.0,
						commenterId: comment.commenter,
					};

					// Save pinned comment to session transcript for persistence
					this.sessionManager.addTranscriptSegment(this.currentSessionId, {
						id: segmentId,
						start: elapsed,
						end: elapsed,
						text: comment.content,
						confidence: 1.0,
						isFinal: true,
						type: "comment",
						commenterId: comment.commenter,
					});

					this.callbacks.onTranscriptionUpdate(segment);
				}
			}
		};

		this.commentSystem.onCommentsGenerated = (comments) => {
			if (!this.currentSessionId) return;
			for (const comment of comments) {
				this.sessionManager.addComment(this.currentSessionId, comment);
			}
			// Create a new array reference to ensure React detects the change
			const allComments =
				this.sessionManager.getCurrentSession(DEFAULT_USER_ID)?.comments || [];
			this.callbacks.onCommentsUpdate([...allComments]);
		};

		this.commentSystem.onCommentsClear = () => {
			this.callbacks.onCommentsUpdate([]);
		};
	}

	private async applyInitialPreferences(): Promise<void> {
		await this.commentSystem.initializePreferences(
			this.storage,
			DEFAULT_USER_ID,
			"init",
		);
		const prefs =
			await this.preferenceManager.initializePreferences(DEFAULT_USER_ID);

		// Apply audio config if present
		if (prefs.audioConfig) {
			this.audioAnalyzer.updateConfig(prefs.audioConfig);
		}

		// 保存済みの自動コメント設定をトリガーに反映する
		if (prefs.autoCommentConfig) {
			this.autoCommentTrigger.updateConfig(prefs.autoCommentConfig);
		}

		// 設定変更を subscribe してトリガーにリアルタイム反映
		this.preferenceManager.subscribe((updatedPrefs) => {
			if (updatedPrefs.autoCommentConfig) {
				this.autoCommentTrigger.updateConfig(updatedPrefs.autoCommentConfig);
			}
		});

		this.callbacks.onCommenterDefinitionsUpdate(
			prefs.commenterDefinitions || DEFAULT_COMMENTER_DEFINITIONS,
		);
		this.callbacks.onLowConfidenceThresholdUpdate(
			prefs.lowConfidenceThreshold ?? 0.9,
		);
	}

	private setupInternalEventHandlers(): void {
		// AudioAnalyzer の沈黙検知イベント: VADによるコミット
		this.audioAnalyzer.onSpeechSegment(() => {
			if (this.hasInterimResult) {
				this.voiceManager.commitCurrentTranscript();
				this.hasInterimResult = false;
			}
		});

		// 定期的な分析更新イベント: 自動トリガーの判定に使用
		this.audioAnalyzer.onAnalysis((data) => {
			if (this.isRunning && !this.isPaused) {
				this.autoCommentTrigger.onSilenceDetected(data.silenceDuration);
			}
		});

		this.audioAnalyzer.onVolume((volume) => {
			if (
				this.isRunning &&
				this.isPaused &&
				volume > this.audioAnalyzer.getConfig().autoResumeThreshold
			) {
				if (this.callbacks.onAutoResume) {
					this.callbacks.onAutoResume();
				} else {
					this.callbacks.onStatusChange("自動再開します...", "running");
				}
			}
		});

		this.voiceManager.onTranscript(async (text, isFinal, confidence) => {
			if (!text || !text.trim()) return;

			if (!this.currentTranscriptId) {
				this.currentTranscriptId = `transcript-${Date.now()}`;
				// 音声認識の開始時間を記録。認識の遅延を考慮して少し前（800ms）を起点とする
				this.currentTranscriptStartTime = new Date(Date.now() - 800);
			}

			this.hasInterimResult = !isFinal;
			this.callbacks.onTranscriptionUpdate({
				id: this.currentTranscriptId,
				text,
				isFinal,
				timestamp: this.currentTranscriptStartTime || new Date(),
				type: "speech",
				confidence,
			});

			if (isFinal) {
				// 自動トリガー用に新規テキストを通知
				this.autoCommentTrigger.onNewTranscript(text);

				if (this.currentSessionId && this.currentTranscriptStartTime) {
					const now = new Date();
					const duration =
						now.getTime() - this.currentTranscriptStartTime.getTime();

					this.sessionManager.trackActivity(this.currentSessionId, {
						type: "speech",
						timestamp: this.currentTranscriptStartTime,
						data: {
							transcript: text,
							confidence,
							isFinal: true,
							id: this.currentTranscriptId,
						},
						duration: duration,
					});
				}
				this.currentTranscriptId = null;
				this.currentTranscriptStartTime = null;
			}
		});
	}
}
